import type { JSX, ReactNode } from "react";
import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";

// One collapsible block of the analytics side panel. The header is a single
// button (aria-expanded / aria-controls) carrying the title and a count badge,
// so a collapsed section still says how much is selected inside it. `action`
// sits beside the toggle (not inside it — a button cannot nest a button).
// The body stays mounted while collapsed (only `hidden`), so aria-controls
// always has a target and the controls inside keep their state (the filter
// fields' resolved-name caches would otherwise reset to raw ids).

type CollapsibleSectionProps = {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  action?: ReactNode;
  children: ReactNode;
};

export const CollapsibleSection = ({
  title,
  count,
  defaultOpen = true,
  action,
  children,
}: CollapsibleSectionProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const bodyId = useId();

  return (
    <section className="border-t border-(--border-subtle) py-4">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          className="flex flex-1 items-center gap-2 border-0 bg-transparent p-0 text-left"
        >
          <ChevronDown
            size={15}
            strokeWidth={2}
            className={`text-(--text-muted) transition-transform ${
              isOpen ? "" : "-rotate-90"
            }`}
          />
          <span className="text-[13px] font-semibold text-(--text-primary)">
            {title}
          </span>
          {count !== undefined && count > 0 && (
            <span className="inline-flex min-w-[22px] items-center justify-center rounded-(--radius-pill) bg-(--pastel-violet) px-2 py-0.5 text-[11px] font-bold text-(--brand-primary)">
              {count}
            </span>
          )}
        </button>
        {action}
      </div>
      <div id={bodyId} hidden={!isOpen} className="pt-3">
        {children}
      </div>
    </section>
  );
};
